(() => {
  // Landing Noël v3 : film, dates limites, pluie de perles (physique A), entrée des personnages, pause commune.
  // Spécification : docs/superpowers/specs/2026-10-19-landing-noel-v3-design.md
  const root = document.querySelector('[data-noel]');
  if (!root) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const etat = { pause: false };

  // Dernier jour inclus, heure de Paris ; la première date non passée donne la ligne.
  function ligneDate(dates, maintenant) {
    const jour = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(maintenant);
    for (const date of dates) {
      if (date && date.jusqu && jour <= date.jusqu) return date.ligne || '';
    }
    return '';
  }

  // Le serveur a déjà choisi la ligne du jour ; le script la corrige si la page date d'avant minuit.
  // Une ligne échue garde sa place, cachée : la carte ne saute pas le jour de la date limite.
  root.querySelectorAll('[data-noel-date]').forEach((element) => {
    let dates = [];
    try {
      dates = JSON.parse(element.dataset.dates || '[]');
    } catch (erreur) {
      return;
    }
    const ligne = ligneDate(dates, new Date());
    if (ligne) {
      element.textContent = ligne;
      element.classList.remove('est-passee');
    } else {
      element.classList.add('est-passee');
    }
  });

  // Film : chargé après la page, sans son ; à la fin, il reprend sur sa boucle finale.
  const film = (() => {
    const boite = root.querySelector('[data-noel-film]');
    const video = boite ? boite.querySelector('video') : null;
    if (!video) return { actualiser() {}, enRoute: () => false, quandChange: null };
    const mobile = window.matchMedia('(max-width: 749px)');
    const boucle = Math.max(0, Number(video.dataset.boucle) || 0);
    const api = { actualiser, enRoute, quandChange: null };
    let charge = false;
    let echec = false;
    let visible = true;
    let pret = document.readyState === 'complete';
    let pose = boucle === 0;

    function actif() {
      return pret && visible && !etat.pause && !document.hidden && !reduce.matches && !saveData;
    }
    function enRoute() {
      return charge && !pose && !echec && !video.paused;
    }
    function charger() {
      if (charge) return;
      charge = true;
      video.muted = true;
      video.defaultMuted = true;
      video.src = mobile.matches ? video.dataset.srcMobile : video.dataset.srcDesktop;
    }
    function actualiser() {
      if (actif()) {
        charger();
        const lecture = video.play();
        if (lecture && lecture.catch) lecture.catch(echouer);
      } else if (charge) {
        video.pause();
      }
    }
    // Chaque changement d'état du film prévient la pluie, qui décide seule si elle tombe.
    function prevenir() {
      if (api.quandChange) api.quandChange();
    }
    function poser() {
      if (pose) return;
      pose = true;
      prevenir();
    }
    // Un film illisible ou refusé compte comme posé : l'image fixe reste, la pluie peut tomber.
    function echouer() {
      echec = true;
      boite.dataset.etat = 'image';
      prevenir();
    }

    video.addEventListener('playing', () => { boite.dataset.etat = 'lecture'; prevenir(); });
    video.addEventListener('pause', () => {
      if (boite.dataset.etat === 'lecture') boite.dataset.etat = 'pause';
      prevenir();
    });
    video.addEventListener('error', echouer);
    video.addEventListener('timeupdate', () => { if (boucle && video.currentTime >= boucle) poser(); });
    video.addEventListener('ended', () => {
      poser();
      video.currentTime = boucle;
      if (actif()) video.play().catch(() => {});
    });
    if (!pret) window.addEventListener('load', () => { pret = true; actualiser(); }, { once: true });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entrees) => { visible = entrees[entrees.length - 1].isIntersecting; actualiser(); }).observe(boite);
    }
    return api;
  })();

  // Pluie de perles, physique A choisie par Patrice : chute lente et continue, derrière les cartes.
  // Elle sort du bas de la photo du film et tombe jusqu'au-dessus de la réassurance (retour du 2026-10-20).
  const perles = (() => {
    const canvas = root.querySelector('[data-noel-perles]');
    if (!canvas || !canvas.getContext) return { actualiser() {} };
    const scene = canvas.parentElement;
    const cadre = root.querySelector('[data-noel-film]');
    const photo = root.querySelector('.milaura-noel__film-image');
    const titre = root.querySelector('.milaura-noel__titre');
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const colonnes = Number(canvas.dataset.colonnes) || 5;
    const cases = Number(canvas.dataset.cases) || 20;
    const COTE = 128;
    // Ordre de la planche : améthyste ×2, aventurine ×2, citrine, cornaline ×2, fluorine, grenat ×2,
    // jaspe bleu-vert ×2, lapis-lazuli ×2, malachite ×2, œil de tigre ×2, quartz rose ×2.
    // Les pierres sombres restent rares sur le blanc.
    const POIDS = { 7: 0.7, 8: 0.45, 9: 0.45, 16: 0.6, 17: 0.6 };
    const COUCHES = [
      { r: [5, 7], alpha: 0.65, vitesse: 0.6, part: 0.38, doux: true, ombre: 0 },
      { r: [9, 13], alpha: 1, vitesse: 1, part: 0.44, doux: false, ombre: 0.16 },
      { r: [16, 23], alpha: 1, vitesse: 1.4, part: 0.18, doux: false, ombre: 0.2 }
    ];
    // Au départ, la photo verse plus de perles pendant `elan` secondes pour installer la pluie sans attendre.
    const PHYS = { g: 200, frein: 0.85, balancement: 12, parSeconde: [7, 13], max: [42, 80], elan: 1.6, jaillissement: 2.5 };
    const planche = new Image();
    const cache = new Map();
    const pointeur = { x: -1e4, y: -1e4 };
    let pret = false;
    let largeur = 0;
    let hauteur = 0;
    let billes = [];
    let dernier = 0;
    let enCours = false;
    let visible = false;
    let ratio = 0;
    let rafId = 0;
    let fige = false;
    let attente = 0;
    let elan = PHYS.elan;
    let bande = { gauche: 0, droite: 0 };

    const hasard = (a, b) => a + Math.random() * (b - a);
    const petit = () => largeur < 750;
    const choisirCase = () => {
      for (;;) {
        const i = Math.floor(Math.random() * cases);
        if (Math.random() < (POIDS[i] || 1)) return i;
      }
    };
    const choisirCouche = () => {
      const t = Math.random();
      if (t < COUCHES[0].part) return 0;
      return t < COUCHES[0].part + COUCHES[1].part ? 1 : 2;
    };
    const sprite = (i, px, doux) => {
      const cle = `${i}:${px}:${doux}`;
      if (cache.has(cle)) return cache.get(cle);
      const c = document.createElement('canvas');
      c.width = c.height = px;
      const g = c.getContext('2d');
      g.imageSmoothingQuality = 'high';
      const sx = (i % colonnes) * COTE;
      const sy = Math.floor(i / colonnes) * COTE;
      if (doux) {
        const t = document.createElement('canvas');
        const q = Math.max(3, Math.round(px * 0.45));
        t.width = t.height = q;
        t.getContext('2d').drawImage(planche, sx, sy, COTE, COTE, 0, 0, q, q);
        g.drawImage(t, 0, 0, px, px);
      } else {
        g.drawImage(planche, sx, sy, COTE, COTE, 0, 0, px, px);
      }
      cache.set(cle, c);
      return c;
    };
    // Ombre douce sous chaque perle : sans elle, une perle sur blanc paraît collée à plat.
    const ombre = (px) => {
      const cle = `o:${px}`;
      if (cache.has(cle)) return cache.get(cle);
      const c = document.createElement('canvas');
      c.width = Math.round(px * 1.5);
      c.height = Math.round(px * 0.9);
      const g = c.getContext('2d');
      const encre = getComputedStyle(root).getPropertyValue('--milaura-prune').trim() || 'black';
      g.translate(c.width / 2, c.height / 2);
      g.scale(1, 0.55);
      const rayon = c.width / 2;
      const degrade = g.createRadialGradient(0, 0, 0, 0, 0, rayon);
      degrade.addColorStop(0, encre);
      degrade.addColorStop(1, 'transparent');
      g.fillStyle = degrade;
      g.beginPath();
      g.arc(0, 0, rayon, 0, Math.PI * 2);
      g.fill();
      cache.set(cle, c);
      return c;
    };
    const ajouter = (initiale) => {
      const l = choisirCouche();
      const couche = COUCHES[l];
      const r = Math.round(hasard(couche.r[0], couche.r[1]) * (petit() ? 0.9 : 1.15));
      billes.push({ l, r, i: choisirCase(), x: hasard(bande.gauche + r, bande.droite - r), y: initiale ? hasard(0, hauteur) : hasard(-40, -r),
        vx: hasard(-8, 8), vy: hasard(40, 90) * couche.vitesse, phase: Math.random() * 6.28 });
    };
    // Le haut du canvas rejoint le bas de la photo du film ; les perles naissent sous sa largeur.
    const mesurer = () => {
      const cadreRect = cadre ? cadre.getBoundingClientRect() : null;
      if (cadreRect) canvas.style.setProperty('--noel-pluie-haut', `${Math.max(0, Math.round(scene.getBoundingClientRect().top - cadreRect.bottom))}px`);
      const rect = canvas.getBoundingClientRect();
      largeur = rect.width;
      hauteur = rect.height;
      bande = cadreRect
        ? { gauche: Math.max(0, cadreRect.left - rect.left), droite: Math.min(largeur, cadreRect.right - rect.left) }
        : { gauche: 0, droite: largeur };
      if (bande.droite - bande.gauche < 60) bande = { gauche: 0, droite: largeur };
      canvas.width = Math.round(largeur * dpr);
      canvas.height = Math.round(hauteur * dpr);
      if (!enCours) dessiner();
    };
    const remplir = (n) => {
      billes = [];
      for (let k = 0; k < n; k += 1) ajouter(true);
    };
    const avancer = (dt) => {
      const max = PHYS.max[petit() ? 0 : 1];
      attente += dt * PHYS.parSeconde[petit() ? 0 : 1] * (elan > 0 ? PHYS.jaillissement : 1);
      elan = Math.max(0, elan - dt);
      while (attente >= 1) {
        attente -= 1;
        if (billes.length < max) ajouter(false);
      }
      for (const b of billes) {
        const couche = COUCHES[b.l];
        b.vy += PHYS.g * couche.vitesse * dt;
        b.vy -= b.vy * PHYS.frein * dt;
        b.phase += dt * 1.3;
        b.vx += Math.sin(b.phase) * PHYS.balancement * dt;
        const dx = b.x - pointeur.x;
        const dy = b.y - pointeur.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 110 * 110) {
          const d = Math.sqrt(d2) || 1;
          const force = (1 - d / 110) * 900 * dt;
          b.vx += (dx / d) * force;
          b.vy += (dy / d) * force * 0.6;
        }
        b.vx *= Math.pow(0.6, dt);
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        if (b.x < -b.r) b.x = largeur + b.r;
        else if (b.x > largeur + b.r) b.x = -b.r;
      }
      billes = billes.filter((b) => b.y - b.r < hauteur + 2);
    };
    const dessiner = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, largeur, hauteur);
      if (!pret) return;
      for (let l = 0; l < COUCHES.length; l += 1) {
        const couche = COUCHES[l];
        for (const b of billes) {
          if (b.l !== l) continue;
          // Pas de fondu en haut : le bord du canvas, au ras de la photo, découpe les perles qui en sortent.
          const bord = Math.min(1, Math.max(0, (hauteur - b.y) / 60));
          const px = Math.max(4, Math.round(b.r * 2 * dpr));
          if (couche.ombre) {
            ctx.globalAlpha = couche.ombre * bord;
            ctx.drawImage(ombre(px), b.x - b.r * 0.5, b.y + b.r * 0.55, b.r * 1.5, b.r * 0.9);
          }
          ctx.globalAlpha = couche.alpha * bord;
          ctx.drawImage(sprite(b.i, px, couche.doux), b.x - b.r, b.y - b.r, b.r * 2, b.r * 2);
        }
      }
      ctx.globalAlpha = 1;
    };
    const boucler = (t) => {
      rafId = 0;
      if (!enCours) return;
      const dt = Math.min((t - dernier) / 1000 || 0, 1 / 30);
      dernier = t;
      avancer(dt);
      dessiner();
      rafId = window.requestAnimationFrame(boucler);
    };
    // Une seule animation principale : la pluie attend que le film se pose, sauf si les portes occupent l'écran.
    const actif = () => pret && visible && !etat.pause && !document.hidden && !reduce.matches && (!film.enRoute() || ratio >= 0.5);
    // Une seule boucle à la fois : l'image en attente est annulée à chaque arrêt.
    function arreter() {
      enCours = false;
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }
    function actualiser() {
      if (reduce.matches) {
        arreter();
        if (pret) {
          if (!fige) {
            remplir(12);
            fige = true;
          }
          dessiner();
        }
        return;
      }
      fige = false;
      if (!actif()) {
        arreter();
        return;
      }
      if (!enCours) {
        enCours = true;
        dernier = performance.now();
      }
      if (!rafId) rafId = window.requestAnimationFrame(boucler);
    }

    // Aucun remplissage d'un coup : les premières perles sortent de la photo.
    planche.addEventListener('load', () => {
      pret = true;
      mesurer();
      actualiser();
    });
    // La planche part dès que la photo du film est là, en priorité haute : la photo a déjà eu
    // la bande passante, et la pluie n'attend plus les images plus bas dans la page.
    const lancer = () => {
      if (planche.src) return;
      planche.fetchPriority = 'high';
      planche.src = canvas.dataset.sprite;
    };
    if (document.readyState === 'complete' || !photo || photo.complete) {
      lancer();
    } else {
      photo.addEventListener('load', lancer, { once: true });
      photo.addEventListener('error', lancer, { once: true });
      window.addEventListener('load', lancer, { once: true });
    }
    mesurer();
    if ('ResizeObserver' in window) {
      const taille = new ResizeObserver(mesurer);
      taille.observe(scene);
      if (titre) taille.observe(titre);
      if (cadre) taille.observe(cadre);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entrees) => {
        ratio = entrees[entrees.length - 1].intersectionRatio;
        visible = ratio >= 0.15;
        actualiser();
      }, { threshold: [0, 0.15, 0.5, 1] }).observe(canvas);
    } else {
      visible = true;
    }
    root.addEventListener('pointermove', (evenement) => {
      if (evenement.pointerType !== 'mouse') return;
      const rect = canvas.getBoundingClientRect();
      pointeur.x = evenement.clientX - rect.left;
      pointeur.y = evenement.clientY - rect.top;
    });
    root.addEventListener('pointerleave', () => { pointeur.x = -1e4; pointeur.y = -1e4; });
    return { actualiser };
  })();

  // La pluie attend que le film se pose sur le salon : une seule animation principale à l'écran.
  film.quandChange = () => perles.actualiser();

  // Entrée unique des personnages, quand les deux cartes arrivent à l'écran.
  const portes = root.querySelector('.milaura-noel__portes');
  const grille = root.querySelector('.milaura-noel__portes-grille');
  if (portes && grille) {
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entrees, observateur) => {
        if (!entrees.some((entree) => entree.isIntersecting)) return;
        portes.classList.add('est-vue');
        observateur.disconnect();
      }, { threshold: 0.12 }).observe(grille);
    } else {
      portes.classList.add('est-vue');
    }
  }

  // Photos du catalogue : la grille reste masquée (une grille masquée ne télécharge pas ses images) jusqu'à
  // la fin du chargement, l'approche du visiteur ou un lien vers un groupe ; l'image du film passe d'abord.
  // Grille masquée, le catalogue commence 450 à 540 px sous l'écran : marge d'approche de 200 px.
  const catalogue = root.querySelector('.milaura-noel__catalogue');
  if (catalogue) {
    let approche = null;
    const montrer = () => {
      catalogue.classList.add('est-proche');
      if (approche) approche.disconnect();
    };
    if (document.readyState === 'complete' || /^#milaura-noel-/.test(window.location.hash || '')) {
      montrer();
    } else {
      window.addEventListener('load', montrer, { once: true });
      if ('IntersectionObserver' in window) {
        approche = new IntersectionObserver((entrees) => {
          if (entrees.some((entree) => entree.isIntersecting)) montrer();
        }, { rootMargin: '0px 0px 200px 0px' });
        approche.observe(catalogue);
      }
      // Le clic arrive avant le saut vers l'ancre : la grille est là quand la page défile.
      root.addEventListener('click', (evenement) => {
        if (evenement.target.closest && evenement.target.closest('a[href^="#milaura-noel-"]')) montrer();
      });
    }
  }

  // Menu clair tant qu'il passe sur le film, comme sur les landings collection ; le film passe
  // sous le menu sur ordinateur (version B) et sur téléphone (2026-10-27).
  const navigation = document.querySelector('[data-milaura-navigation]');
  const filmBoite = root.querySelector('[data-noel-film]');
  if (navigation && filmBoite) {
    let tonId = 0;
    const tonMenu = () => {
      tonId = 0;
      const surFilm = filmBoite.getBoundingClientRect().bottom > navigation.getBoundingClientRect().bottom;
      navigation.dataset.collectionHeroTone = surFilm ? 'light' : 'dark';
    };
    const prevoirTon = () => { if (!tonId) tonId = window.requestAnimationFrame(tonMenu); };
    window.addEventListener('scroll', prevoirTon, { passive: true });
    window.addEventListener('resize', prevoirTon);
    tonMenu();
  }

  const pauseBtn = root.querySelector('[data-noel-pause]');
  if (pauseBtn) {
    // Libellé fixe ; l'état passe par aria-pressed.
    pauseBtn.addEventListener('click', () => {
      etat.pause = !etat.pause;
      pauseBtn.setAttribute('aria-pressed', String(etat.pause));
      film.actualiser();
      perles.actualiser();
    });
  }

  document.addEventListener('visibilitychange', () => {
    film.actualiser();
    perles.actualiser();
  });
  reduce.addEventListener('change', () => {
    film.actualiser();
    perles.actualiser();
  });

  // Tout est en place : le script en ligne de la section garde sa marque (sinon, elle tombe au chargement).
  root.dataset.noelPret = '1';
})();
